/**
 * Cookie notice — see snippets/cookie-consent.liquid.
 *
 * Informs once per browser; OK or × closes it for good and changes nothing (× in Settings discards). Left alone, it closes itself
 * after 4 s on screen and counts as seen (the clock pauses while it is covered, tucked, hovered or
 * focused, and stops once Settings is opened).
 * "Settings" (under OK) opens Analytics / Marketing switches, both on by default because that is
 * what the store does; saving records the choice through Shopify's Customer Privacy API (web
 * pixels, the Kuwait Clarity app) and in localStorage 'cc-analytics' (the hardcoded Clarity
 * loaders on the other stores), and removes cookies of anything switched off.
 *  - never in the AppMe webview (.is-webview, set in layout/theme.liquid);
 *  - steps aside when Shopify's own privacy banner is due or was already answered, so no one sees two;
 *  - slides in on the first scroll/tap or after a few seconds, so it never competes with page load;
 *  - tucks away while the visitor scrolls down and comes back when they scroll up or pause.
 */
if (!customElements.get('cookie-consent')) {
  const DISMISSED_KEY = 'cc-cookie-notice';
  const ANALYTICS_KEY = 'cc-analytics';
  const SHOW_DELAY = 4000;
  const UNTUCK_DELAY = 1200;
  const CLOSE_FALLBACK = 450;
  const API_TIMEOUT = 3000;
  const AUTO_HIDE = 4000;
  const AUTO_HIDE_TICK = 250;
  const ANALYTICS_COOKIES = /^(_ga|_gid|_gat|_clck|_clsk|_hj)/;
  const MARKETING_COOKIES = /^(_fbp|_fbc|_gcl|_uetsid|_uetvid|_tt_|_ttp|_scid|_sctr|_pin_unauth)/;

  const store = {
    get(key) {
      try {
        return window.localStorage.getItem(key);
      } catch (e) {
        return null;
      }
    },
    set(key, value) {
      try {
        if (value === null) window.localStorage.removeItem(key);
        else window.localStorage.setItem(key, value);
      } catch (e) {
        // Storage blocked: the notice simply shows again on the next visit.
      }
    }
  };

  class CookieConsent extends HTMLElement {
    connectedCallback() {
      this.card = this.querySelector('.cookie-consent__card');
      this.prefs = this.querySelector('.cookie-consent__prefs');
      this.okBtn = this.querySelector('[data-cookie-ok]');
      this.saveBtn = this.querySelector('[data-cookie-save]');
      this.settingsBtn = this.querySelector('[data-cookie-settings]');
      this.analyticsInput = this.querySelector('input[name="analytics"]');
      this.marketingInput = this.querySelector('input[name="marketing"]');

      this.okBtn.addEventListener('click', () => this.dismiss());
      this.querySelector('[data-cookie-close]').addEventListener('click', () => this.dismiss());
      this.saveBtn.addEventListener('click', () => this.save());
      this.settingsBtn.addEventListener('click', () => this.openSettings());
      this.card.addEventListener('pointerenter', () => { this.holding = true; });
      this.card.addEventListener('pointerleave', () => { this.holding = false; });

      if (document.documentElement.classList.contains('is-webview') || store.get(DISMISSED_KEY) === '1') return;
      CookieConsent.privacyApi((api) => {
        this.api = api;
        if (api && CookieConsent.shopifyHandles(api)) return;
        this.settingsBtn.hidden = !api; // switches need the API to mean anything
        this.scheduleShow();
      });
    }

    /**
     * Calls back once with Shopify's Customer Privacy API, or null when it is missing,
     * fails or is slow — the notice itself never depends on it.
     */
    static privacyApi(callback) {
      let answered = false;
      const answer = (api) => {
        if (answered) return;
        answered = true;
        callback(api);
      };
      const shopify = window.Shopify;
      if (shopify && shopify.customerPrivacy) {
        answer(shopify.customerPrivacy);
        return;
      }
      if (!shopify || typeof shopify.loadFeatures !== 'function') {
        answer(null);
        return;
      }
      setTimeout(() => answer(null), API_TIMEOUT);
      try {
        shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
          answer(!error && window.Shopify.customerPrivacy ? window.Shopify.customerPrivacy : null);
        });
      } catch (e) {
        answer(null);
      }
    }

    /** True when Shopify's own banner is due, or the visitor already answered it. */
    static shopifyHandles(api) {
      try {
        if (typeof api.shouldShowBanner === 'function' && api.shouldShowBanner()) return true;
        const consent = typeof api.currentVisitorConsent === 'function' ? api.currentVisitorConsent() : {};
        return Boolean(consent.analytics || consent.marketing);
      } catch (e) {
        return false;
      }
    }

    scheduleShow() {
      const events = ['scroll', 'pointerdown', 'keydown'];
      const show = () => {
        clearTimeout(timer);
        events.forEach((name) => window.removeEventListener(name, show));
        this.show();
      };
      const timer = setTimeout(show, SHOW_DELAY);
      events.forEach((name) => window.addEventListener(name, show, { passive: true, once: true }));
    }

    show() {
      if (store.get(DISMISSED_KEY) === '1') return;
      this.hidden = false;
      requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-open')));
      this.watchScroll();
      this.startAutoHide();
    }

    /** Counts only time actually on screen; dismisses (counts as seen) after AUTO_HIDE. */
    startAutoHide() {
      if (this.autoHideTimer) return;
      let seen = 0;
      this.autoHideTimer = setInterval(() => {
        const onScreen = this.classList.contains('is-open') && !this.classList.contains('is-tucked')
          && getComputedStyle(this).display !== 'none'
          && getComputedStyle(this.card).transform === 'none';
        const busy = this.holding || this.contains(document.activeElement);
        if (onScreen && !busy) seen += AUTO_HIDE_TICK;
        if (seen >= AUTO_HIDE) this.dismiss();
      }, AUTO_HIDE_TICK);
    }

    stopAutoHide() {
      clearInterval(this.autoHideTimer);
      this.autoHideTimer = null;
    }

    /** Switches show what is actually allowed right now (on, unless the visitor turned it off). */
    openSettings() {
      if (!this.api) return;
      this.stopAutoHide();
      try {
        this.analyticsInput.checked = this.api.analyticsProcessingAllowed();
        this.marketingInput.checked = this.api.marketingAllowed();
      } catch (e) {
        // Keep the markup defaults (on).
      }
      this.prefs.hidden = false;
      this.classList.add('has-prefs');
      this.saveBtn.hidden = false;
      this.okBtn.hidden = true;
      this.settingsBtn.hidden = true;
      this.settingsBtn.setAttribute('aria-expanded', 'true');
      this.setTucked(false);
      this.analyticsInput.focus({ preventScroll: true });
    }

    save() {
      const analytics = this.analyticsInput.checked;
      const marketing = this.marketingInput.checked;
      store.set(ANALYTICS_KEY, analytics ? null : 'no');
      if (!analytics) {
        try {
          if (typeof window.clarity === 'function') {
            window.clarity('consentv2', { ad_Storage: 'denied', analytics_Storage: 'denied' });
          }
        } catch (e) {
          // Clarity not loaded or older API: the cookies below are removed anyway.
        }
        CookieConsent.clearCookies(ANALYTICS_COOKIES);
      }
      if (!marketing) CookieConsent.clearCookies(MARKETING_COOKIES);
      if (this.api) {
        try {
          this.api.setTrackingConsent(
            { analytics, marketing, preferences: true, sale_of_data: marketing },
            () => {
              // Trackers still running may have re-set a cookie before the choice landed.
              if (!analytics) CookieConsent.clearCookies(ANALYTICS_COOKIES);
              if (!marketing) CookieConsent.clearCookies(MARKETING_COOKIES);
            }
          );
        } catch (e) {
          // The localStorage flag and cookie removal above still apply.
        }
      }
      this.dismiss();
    }

    dismiss() {
      this.stopAutoHide();
      store.set(DISMISSED_KEY, '1');
      this.close();
    }

    close() {
      const hadFocus = this.contains(document.activeElement);
      this.classList.remove('is-open');
      this.setTucked(false);
      window.removeEventListener('scroll', this.scrollHandler);

      // Hide once the slide-out ends; the timer covers no transition at all
      // (reduced motion, or the notice display:none behind an open drawer).
      let finished = false;
      const done = () => {
        if (finished) return;
        finished = true;
        clearTimeout(timer);
        this.card.removeEventListener('transitionend', onEnd);
        this.hidden = true;
      };
      const onEnd = (evt) => {
        if (evt.target === this.card && evt.propertyName === 'transform') done();
      };
      this.card.addEventListener('transitionend', onEnd);
      const timer = setTimeout(done, CLOSE_FALLBACK);

      if (hadFocus) {
        const main = document.getElementById('main-content');
        if (main) {
          if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
          main.focus({ preventScroll: true });
        }
      }
    }

    /** Tuck the notice away while scrolling down; bring it back on scroll up or after a pause. */
    watchScroll() {
      if (this.scrollHandler) return;
      let lastY = window.scrollY;
      let untuckTimer;
      this.scrollHandler = () => {
        const y = window.scrollY;
        if (y > lastY + 6 && y > 160 && this.prefs.hidden) this.setTucked(true);
        else if (y < lastY - 6) this.setTucked(false);
        lastY = y;
        clearTimeout(untuckTimer);
        untuckTimer = setTimeout(() => this.setTucked(false), UNTUCK_DELAY);
      };
      window.addEventListener('scroll', this.scrollHandler, { passive: true });
    }

    /** Tucked = off-screen, so also out of the tab order. */
    setTucked(tucked) {
      this.classList.toggle('is-tucked', tucked);
      this.card.inert = tucked;
    }

    /** Expire first-party cookies matching `pattern` on this host and its parent domain. */
    static clearCookies(pattern) {
      const host = window.location.hostname;
      const parent = host.split('.').slice(-2).join('.');
      const domains = ['', `; domain=${host}`, `; domain=.${parent}`];
      document.cookie.split(';').forEach((pair) => {
        const name = pair.split('=')[0].trim();
        if (!pattern.test(name)) return;
        domains.forEach((domain) => {
          document.cookie = `${name}=; Max-Age=0; path=/${domain}`;
        });
      });
    }
  }

  customElements.define('cookie-consent', CookieConsent);
}
